/**
 * Supercapacitor Module Design Panel Component
 * Displays IECON2023 design parameters: U_C0max, U_C0min, U_C0inM, C0
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  FlashOn,
  TrendingUp,
  TrendingDown,
  Engineering,
  Info
} from '@mui/icons-material';
import { SupercapDesign } from '../types/api';

interface SupercapDesignPanelProps {
  supercapDesign: SupercapDesign | null;
}

const SupercapDesignPanel: React.FC<SupercapDesignPanelProps> = ({ supercapDesign }) => {
  const theme = useTheme();

  // If no supercap design data, don't render
  if (!supercapDesign || !supercapDesign.U_C0max_V) {
    return null;
  }

  const parameters = [
    {
      label: 'Maximale Modulspannung',
      value: supercapDesign.U_C0max_V,
      unit: 'V',
      key: 'U_C0max',
      icon: <TrendingUp />,
      color: theme.palette.success.main,
      description: 'Obere Betriebsspannungsgrenze des Superkondensator-Moduls'
    },
    {
      label: 'Minimale Modulspannung',
      value: supercapDesign.U_C0min_V,
      unit: 'V',
      key: 'U_C0min',
      icon: <TrendingDown />,
      color: theme.palette.info.main,
      description: 'Untere Betriebsspannungsgrenze des Superkondensator-Moduls'
    },
    {
      label: 'Zwischenspannung',
      value: supercapDesign.U_C0inM_V,
      unit: 'V',
      key: 'U_C0inM',
      icon: <FlashOn />,
      color: theme.palette.warning.main,
      description: 'Mittlere Betriebsspannung für Auslegung und Regelung (Gleichung 16)'
    },
    {
      label: 'Modulkapazität',
      value: supercapDesign.C0_F,
      unit: 'F',
      key: 'C0',
      icon: <Engineering />,
      color: theme.palette.secondary.main,
      description: 'Erforderliche Kapazität für End-of-Life-Betrieb (Gleichung 21)'
    }
  ];

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)'
          : 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Engineering sx={{ fontSize: 32, color: theme.palette.secondary.main, mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Superkondensator-Modul Auslegung
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {supercapDesign.description || 'IECON2023 Design-Parameter'}
          </Typography>
        </Box>
        <Tooltip title="Basierend auf IECON 2023 Paper: Design Criteria for Super-Capacitor Modules">
          <Chip
            label="IECON2023"
            size="small"
            color="secondary"
            icon={<Info />}
            sx={{ fontWeight: 600 }}
          />
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Parameter Grid */}
      <Grid container spacing={2}>
        {parameters.map((param) => (
          <Grid item xs={12} sm={6} md={3} key={param.key}>
            <Tooltip title={param.description} arrow placement="top">
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  height: '100%',
                  borderRadius: 2,
                  border: `2px solid ${alpha(param.color, 0.3)}`,
                  transition: 'all 0.3s ease',
                  cursor: 'help',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${alpha(param.color, 0.25)}`,
                    borderColor: param.color
                  }
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: alpha(param.color, 0.15),
                    mb: 2
                  }}
                >
                  {React.cloneElement(param.icon, {
                    sx: { fontSize: 28, color: param.color }
                  })}
                </Box>

                {/* Label */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  {param.label}
                </Typography>

                {/* Value */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: param.color
                    }}
                  >
                    {param.value !== undefined && param.value !== null
                      ? param.value.toFixed(2)
                      : 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {param.unit}
                  </Typography>
                </Box>
              </Paper>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {/* References Footer */}
      {supercapDesign.references && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {supercapDesign.references.U_bus && (
              <Chip
                label={`DC-Bus: ${supercapDesign.references.U_bus}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
            {supercapDesign.references.K_factor && (
              <Chip
                label={`K-Faktor: ${supercapDesign.references.K_factor}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
            {supercapDesign.references.paper && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Quelle: {supercapDesign.references.paper}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default SupercapDesignPanel;
